import {createApp, ref} from 'vue';
import {connectVue} from '../modules/configureBugsnag';
import configureGlobalComponents from '../modules/configureGlobalComponents';
import axiosClient from '../modules/axiosClient';
import formatThousandsWithRounding from '../modules/formatThousandsWithRounding';
import RangeSlider from '@vueform/slider';
import useVuelidate from '@vuelidate/core';
import {helpers, required} from '@vuelidate/validators';
import {postcode} from '../modules/regitValidators';
import {debounce} from '../utils/debounce';
import {CancelToken} from 'axios';


declare interface Models {
    [key: string]: string,
}

const COUNT_DEBOUNCE_MS = 350;

const app = createApp({
    data() {
        return {
            searchType: 'cash',
            carModels: {} as Models,
            make: '',
            model: '',
            loading: false,
            postcode: '',
            minprice: '',
            maxprice: '',
            minmonthly: '',
            maxmonthly: '',
            resultCount: 0,
            resultUpdating: false,
            cancelToken: null as CancelToken|null,
            debouncedFetchCount: (() => {}) as () => void,

            priceOptions: [
                {value: '3000', text: '3000'},
                {value: '4000', text: '4000'},
                {value: '5000', text: '5000'},
                {value: '6000', text: '6000'},
                {value: '7000', text: '7000'},
                {value: '8000', text: '8000'},
                {value: '9000', text: '9000'},
                {value: '10000', text: '10,000'},
                {value: '12500', text: '12,500'},
                {value: '15000', text: '15,000'},
                {value: '17500', text: '17,500'},
                {value: '20000', text: '20,000'},
                {value: '25000', text: '25,000'},
                {value: '30000', text: '30,000'},
                {value: '35000', text: '35,000'},
                {value: '40000', text: '40,000'},
                {value: '45000', text: '45,000'},
                {value: '50000', text: '50,000'},
                {value: '55000', text: '55,000'},
                {value: '60000', text: '60,000'},
                {value: '75000', text: '75,000'},
                {value: '100000', text: '100,000'},
                {value: '250000', text: '250,000'},
                {value: '500000', text: '500,000'},
                {value: '1000000', text: '1,000,000'},
            ],
            monthlyPriceOptions: [
                {value: '1', text: '1'},
                {value: '100', text: '100'},
                {value: '125', text: '125'},
                {value: '150', text: '150'},
                {value: '175', text: '175'},
                {value: '200', text: '200'},
                {value: '250', text: '250'},
                {value: '300', text: '300'},
                {value: '350', text: '350'},
                {value: '400', text: '400'},
                {value: '450', text: '450'},
                {value: '500', text: '500'},
                {value: '550', text: '550'},
                {value: '600', text: '600'},
                {value: '700', text: '700'},
                {value: '800', text: '800'},
                {value: '900', text: '900'},
                {value: '1000', text: '1000'},
            ],
        };
    },
    props: [
        'version',
        'offerType',
        'totalCount',
    ],
    setup() {
        return {
            v$: useVuelidate(),
        };
    },
    validations() {
        if (this.offerType === 'used') {
            return {
                postcode: {
                    required: helpers.withMessage('Please enter your postcode', required),
                    postcode: helpers.withMessage('Value is not a valid postcode', postcode),
                },
            };
        }
        return {};
    },
    watch: {
        make() {
            this.carModels = {};
            this.getModels();
            this.updateCount();
        },
    },
    computed: {
        vehicleModels(): DropdownOptions {
            const dropdown = [] as DropdownOptions;
            Object.keys(this.carModels).forEach((index) => {
                const model = this.carModels[index].toUpperCase();
                dropdown.push({value: index, text: model});
            });
            return dropdown;
        },
        /**
         * @return {boolean}
         */
        isMinGreaterThanMax(): boolean {
            if (this.searchType === 'cash') {
                return parseInt(this.minprice) > parseInt(this.maxprice) && parseInt(this.maxprice) != -1;
            }

            return parseInt(this.minmonthly) > parseInt(this.maxmonthly) && parseInt(this.maxmonthly) != -1;
        },
    },
    methods: {
        formatThousandsWithRounding,
        getModels: function() {
            if (this.make === '') {
                return;
            }
            const getModelsUrl = '/api/car-search/models/' + this.offerType + '/' + this.make;
            this.loading = true;
            this.model = '';
            axiosClient.get(getModelsUrl).then((response) => {
                this.carModels = response.data;
                this.loading = false;
            }, () => {
            });
        },
        async submitUsedForm(e: Event) {
            e.stopPropagation();
            e.preventDefault();

            const result = await this.v$.$validate();

            if (result || !['used', 'finance'].includes(this.offerType)) {
                window.location.href = this.getUrl();
            }
        },
        getUrl() {
            let makeUrl = '/' + this.offerType + '-cars/';
            if (this.make && this.model) {
                makeUrl += this.make.toLowerCase().replace(' ', '-') + '_' + this.model.toLowerCase().replace(' ', '-') + '/';
            } else if (this.make) {
                makeUrl += this.make.toLowerCase().replace(' ', '-') + '/';
            } else {
                makeUrl += 'all/';
            }

            const linkPieces = [];
            if (this.postcode && this.offerType === 'used') {
                linkPieces.push('postcode-' + this.postcode.toLowerCase().replace(' ', '') + '_range-40');
            }

            if (this.searchType === 'monthly') {
                linkPieces.push('pricetype-finance');
                if (this.minmonthly) {
                    linkPieces.push('minmonthly-' + this.minmonthly);
                }

                if (this.maxmonthly) {
                    linkPieces.push('maxmonthly-' + this.maxmonthly);
                }
                linkPieces.push('maintenanceincluded-false');
            } else {
                linkPieces.push('pricetype-cash');
                if (this.minprice) {
                    linkPieces.push('minprice-' + this.minprice);
                }

                if (this.maxprice) {
                    linkPieces.push('maxprice-' + this.maxprice);
                }
            }

            return makeUrl + linkPieces.join('_');
        },
        updateCount() {
            this.resultUpdating = true;
            this.debouncedFetchCount();
        },
        fetchCount() {
            // The API ignores an unparseable postcode and answers with the
            // unfiltered count, so a half-typed one is worse than no request.
            if (this.postcode && !postcode(this.postcode)) {
                this.resultUpdating = false;

                return;
            }

            const url = this.getUrl();

            if (this.cancelToken) {
                this.cancelToken.cancel();
            }
            this.cancelToken = axiosClient.CancelToken.source();
            axiosClient.get('/api/car-search/count' + url, {cancelToken: this.cancelToken.token}).then((response) => {
                this.resultCount = response.data.totalRows || 0;
                this.resultUpdating = false;
            }, (error) => {
                // A cancel means a newer request is already in flight; zeroing
                // the count here would blank it while that one is loading.
                if (axiosClient.isCancel(error)) {
                    return;
                }

                this.resultCount = 0;
                this.resultUpdating = false;
            });
        },
    },
    created() {
        this.debouncedFetchCount = debounce(this.fetchCount, COUNT_DEBOUNCE_MS);
    },
    mounted() {
        const currentUrl = window.location.href;
        const isNewCars = currentUrl.includes('new-cars');
        this.searchType = isNewCars ? 'monthly' : 'cash';

        this.resultCount = this.totalCount;
    },
}, window.propsData);
connectVue(app);
configureGlobalComponents(app);
app.mount('#app--used--landing');

const financeTool = createApp({
    components: {
        'range-slider': RangeSlider,
    },
    data() {
        return {
            cashPrice: 5000,
            monthlyPrice: 85,
        };
    },
    methods: {
        onCashChanged(amount: number) {
            const amountThousands = amount / 1000;
            const monthly =
            181 * Math.pow(amountThousands, 6) / 189000000 -
            11 * Math.pow(amountThousands, 5) / 78750 +
            11471 * Math.pow(amountThousands, 4) / 1512000 -
            4727 * Math.pow(amountThousands, 3) / 25200 +
            149461 * Math.pow(amountThousands, 2) / 75600 +
            293 * amountThousands / 28 +
            50 / 21;

            this.monthlyPrice = Math.round(monthly);
        },
        onMonthlyChanged(amount: number) {
            const cash = -1699996429 * Math.pow(amount, 6) / 236265613471663776000 +
            803593506613 * Math.pow(amount, 5) / 47253122694332755200 -
            742660456001 * Math.pow(amount, 4) / 48217472137074240 +
            3159447730025003 * Math.pow(amount, 3) / 472531226943327552 -
            26937539461075 * Math.pow(amount, 2) / 19189864641948 +
            618988383012090625 * amount / 3281466853773108 -
            2719633027075000 / 637425573771;

            this.cashPrice = Math.round(cash);
        },
        seeCashOffers() {
            const priceRanges = [
                [5000, 6000],
                [6000, 7000],
                [7000, 8000],
                [8000, 9000],
                [9000, 10000],
                [10000, 12500],
                [12500, 15000],
                [15000, 17500],
                [17500, 20000],
                [20000, 25000],
                [25000, 30000],
                [30000, 35000],
                [35000, 40000],
                [40000, 45000],
                [45000, 50000],
            ];


            let min = 5000;
            let max = 50000;
            priceRanges.forEach((range) => {
                if (this.cashPrice >= range[0] && this.cashPrice < range[1]) {
                    min = range[0];
                    max = range[1];
                }
            });

            window.location.href = `/used-cars/all/pricetype-cash_minprice-${min}_maxprice-${max}`;
        },
        seeMonthlyOffers() {
            const priceRanges = [
                [1, 100],
                [100, 125],
                [125, 150],
                [150, 175],
                [175, 200],
                [200, 250],
                [250, 300],
                [300, 350],
                [350, 400],
                [400, 450],
                [450, 500],
                [500, 550],
                [550, 600],
                [600, 700],
                [700, 800],
            ];

            let min = 1;
            let max = 800;
            priceRanges.forEach((range) => {
                if (this.monthlyPrice >= range[0] && this.monthlyPrice < range[1]) {
                    min = range[0];
                    max = range[1];
                }
            });

            window.location.href = `/used-cars/all/pricetype-finance_minmonthly-${min}_maxmonthly-${max}`;
        },
    },
});

let initializationAttempts = 0;
const MAX_ATTEMPTS = 3;

function initializeCalculator() {
    const container = document.getElementById('cf247-calculator-container');
    const existingScript = document.getElementById('cf247-calculator-script');

    if (container && !existingScript && initializationAttempts < MAX_ATTEMPTS) {
        initializationAttempts++;

        try {
            const script = document.createElement('script');
            script.id = 'cf247-calculator-script';
            script.src = 'https://partner-calculator.carfinance247.co.uk/widgets/calculator.js';
            script.setAttribute('data-aid', '43745');
            script.setAttribute('data-utms', '&utm_medium=referral&utm_source=regit&utm_campaign=calculator');

            script.async = true;

            script.addEventListener('load', () => {
                container.dataset.initialized = 'true';
            });

            script.addEventListener('error', () => {
                console.error('Failed to load calculator script');
                script.remove();
                setTimeout(initializeCalculator, 1000);
            });

            container.appendChild(script);
        } catch (error) {
            console.error('Error initializing calculator:', error);
        }
    }
}

window.addEventListener('DOMContentLoaded', initializeCalculator);

connectVue(financeTool);
configureGlobalComponents(financeTool);
financeTool.mount('#finance-tool');


const postcodeSearch = createApp({
    data() {
        return {
            rangeOptions: [
                {value: '', text: 'National'},
                {value: '1', text: '1 mile'},
                {value: '2', text: '2 miles'},
                {value: '5', text: '5 miles'},
                {value: '10', text: '10 miles'},
                {value: '20', text: '20 miles'},
                {value: '30', text: '30 miles'},
                {value: '40', text: '40 miles'},
                {value: '50', text: '50 miles'},
                {value: '75', text: '75 miles'},
                {value: '100', text: '100 miles'},
                {value: '150', text: '150 miles'},
                {value: '200', text: '200 miles'},
            ],
        };
    },
    setup(props) {
        const state = {
            postcode: ref(''),
            range: ref(''),
        };

        const rules = {
            postcode: {
                required: helpers.withMessage('Please enter your postcode', required),
                postcode: helpers.withMessage('Value is not a valid postcode', postcode),
            },
        };

        const v = useVuelidate(rules, state);
        async function search(e: Event) {
            e.preventDefault();
            e.stopPropagation();

            const validationPassed = await v.value.$validate();
            if (validationPassed) {
                const cleanPostcode = state.postcode.value.replace(/\s/g, '');

                let url = `/used-cars/all/postcode-${cleanPostcode}_range-${state.range.value}_pricetype-cash`;

                if (state.range.value === '') {
                    url = `/used-cars/all/pricetype-cash_postcode-${cleanPostcode}`;
                }

                window.location.href = url;
            }
        }

        return {
            postcode: state.postcode,
            range: state.range,
            v$: v,
            search,
        };
    },
});
connectVue(postcodeSearch);
configureGlobalComponents(postcodeSearch);
postcodeSearch.mount('#postcode-search');


const dealerSearch = createApp({
    data() {
        return {
            resultCount: 0,
            resultUpdating: false,
            cancelToken: null as CancelToken|null,
        };
    },
    setup(props) {
        const state = {
            postcode: ref(''),
        };

        const rules = {
            postcode: {
                required: helpers.withMessage('Please enter your postcode', required),
                postcode: helpers.withMessage('Value is not a valid postcode', postcode),
            },
        };

        const v = useVuelidate(rules, state);

        return {
            postcode: state.postcode,
            v$: v,
        };
    },
    methods: {
        formatThousandsWithRounding,
        searchPostcode() {
            window.location.href = this.getUrl();
        },
        getUrl() {
            const postcode = this.postcode.replace(/\s/g, '');

            return `/car-dealers/near-${postcode}`;
        },
        updateCount() {
            const valid = this.v$.$validate();
            if (valid) {
                this.resultUpdating = true;

                const params = {
                    postcode: this.postcode,
                };

                if (this.cancelToken) {
                    this.cancelToken.cancel();
                }
                this.cancelToken = axiosClient.CancelToken.source();
                axiosClient.get('/api/dealers-near-postcode-count', {params, cancelToken: this.cancelToken.token}).then((response) => {
                    this.resultCount = response.data;
                    this.resultUpdating = false;
                }, () => {
                    this.resultCount = 0;
                    this.resultUpdating = false;
                });
            }
        },
    },
    watch: {
        postcode() {
            this.updateCount();
        },
    },
});
connectVue(dealerSearch);
configureGlobalComponents(dealerSearch);
dealerSearch.mount('#dealer-search');
